import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import WorkOrderDetailsView, { AllWoFields, prepareFieldLabel, prepareFieldValue } from './WorkOrderDetailsView';
import { Button, FormControl, FormGroup, ControlLabel, ButtonGroup, DropdownButton, MenuItem } from 'react-bootstrap';
import Select from 'react-select';

const SelectContainerDiv = styled.div`
  .wo-filter-btn {
    margin-left: 10px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 5px;
`;

const Title = styled.h4`
  color: #333;
  font-weight: 600;
`;

const LoadingMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #666;
  font-style: italic;
`;

const EmptyMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #999;
  font-style: italic;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  overflow: hidden;
`;

const TableHeader = styled.thead`
  background-color: #f8f9fa;
`;

const TableHeaderCell = styled.th`
  padding: 6px 7px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  font-size: 14px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f8f9fa;
  }

  &:hover {
    background-color: #e9ecef;
  }

  transition: background-color 0.2s ease;
`;

const TableCell = styled.td`
  padding: 6px 7px;
  border-bottom: 1px solid #dee2e6;
  color: #495057;
  font-size: 14px;

  &.wo-memfis {
    width: 100px;
  }
  &.title {
    word-break: break-all;
  }
  &.actions {
    width: 125px;
  }
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  margin-right: 8px;
  border: 1px solid #007bff;
  background-color: ${props => props.primary ? '#007bff' : 'white'};
  color: ${props => props.primary ? 'white' : '#007bff'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.primary ? '#0056b3' : '#007bff'};
    color: white;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:last-child {
    margin-right: 0;
  }
`;

const MappingForMacrosMatchResult = { // <macros props: AllWoFields>
  EPISODE_NUM: 'episode',
  AUDIO_SOUND: 'audioConfiguration',
  COLOR_SPACE: 'colorSpace',
  DYNAMIC_RANGE: 'colorSpace',
  RESOLUTION: 'resolution',
  RESOLUTION_TYPE: 'resolution',
  TEXT_TYPE: 'textless',
  STUDIO: 'clientName',
  VERSION_NUM: 'replacementNumber',
};

const SearchMacrosProps = [ // from  AllWoFields
  'tag',
];

const filterArray = (arr, selectedFilters, macrosProperties) => {
  const keys = Object.keys(selectedFilters);
  if (!arr || !keys.length) {
    return arr;
  }

  return arr.filter(r => keys.every(k => (selectedFilters[k] || []).some(filterValue => {
    const selFilterVal = filterValue.value;
    const filterValStrInLC = String(selFilterVal).toLowerCase();
    const itemFieldValStrInLC = String(prepareFieldValue(r, k)).toLowerCase();
    const res = filterValStrInLC === itemFieldValStrInLC;
    if (res) {
      return true;
    }
    const macrosPropertyKey = Object.keys(MappingForMacrosMatchResult).find(pk => MappingForMacrosMatchResult[pk] === k);
    if (macrosPropertyKey) {
      const propValues = macrosProperties[macrosPropertyKey];
      if (propValues) {
        return propValues.some(valArr => valArr.some(valItem => String(valItem).toLowerCase() === filterValStrInLC) && valArr.some(valItem => String(valItem).toLowerCase() === itemFieldValStrInLC))
      }
    }
    return false;
  })));
};

const sortArray = (arr, {field, direction}) => {
  if (!arr || !field) {
    return arr;
  }

  return arr.sort((ra, rb) => {
    let a = ra[field];
    let b = rb[field];

    if (direction === 'desc') {
      const tmp = a;
      a = b;
      b = tmp;
    }

    if (['createdAt', 'updatedAt'].includes(field)) {
      try {
        a = new Date(a);
        b = new Date(b);
        return (a === b) ? 0 : a < b ? -1 : 1;
      } catch (ignored) {/**/}
    }

    if (field === 'runtime' && (typeof a === 'string' || typeof b === 'string')) {
      try {
        a = +(a || '-1');
        b = +(b || '-1');
      } catch (ignored) {/**/}
    }

    if (typeof a === 'boolean' || typeof b === 'boolean') {
      return (a === b) ? 0 : a ? -1 : 1;
    }

    if (typeof a === 'number' || typeof b === 'number') {
      return (a || 0) - (b || 0);
    }

    return String(a || '').localeCompare(String(b || ''));
  });
};

const sortLabel = (a, b) => prepareFieldLabel(a).localeCompare(prepareFieldLabel(b));

const WorkOrderSearch = ({ workOrderList, onSelectWo, isLoading, onClose, onSearchWorkOrderList, macrosMatchResult, macrosProperties, currentEmailData }) => {
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showNameOrWoMemfis, setShowNameOrWoMemfis] = useState(macrosMatchResult.TITLE || '');
  const [showCode, setShowCode] = useState(macrosMatchResult.SHOW_CODE || '');
  const [episodeNumber, setEpisodeNumber] = useState(macrosMatchResult.EPISODE_NUM || '');

  const [availableFilters, setAvailableFitlers] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [sortingFields, setSortingFields] = useState([]);
  const [sorting, setSorting] = useState({
    field: 'updatedAt',
    direction: 'desc',
  });

  const workOrderSearchMode = selectedWorkOrder === null;

  React.useEffect(() => {
    let availableWOs = workOrderList || [];
    const availableFilters = {};
    const selectedFilters = {};
    let newSortingFields = [];

    if (availableWOs.length > 1) {
      availableWOs.forEach(wo => {
        AllWoFields.forEach(k => {
          if (!['createdAt', 'updatedAt'].includes(k)) {
            const val = prepareFieldValue(wo, k);
            if (!['title', 'description'].includes(k) && val && (!availableFilters[k] || !availableFilters[k].includes(val))) {
              availableFilters[k] = [...availableFilters[k] || [], val].sort();
            }
          }
        });
      });
      Object.entries(MappingForMacrosMatchResult).forEach(([mk, k]) => {
        const val = macrosMatchResult[mk];
        if (val && AllWoFields.includes(k)) {
          if (!availableFilters[k] || !availableFilters[k].some(vi => String(vi).toLowerCase() === String(val).toLowerCase())) {
            availableFilters[k] = [...availableFilters[k] || [], val].sort();
          }
          const afv = availableFilters[k].filter(vi => String(vi).toLowerCase() === String(val).toLowerCase());
          if (afv.length) {
            selectedFilters[k] = afv;
          }
        }
      });
      Object.entries(macrosMatchResult).filter(([k, val]) => val && AllWoFields.includes(k) && !MappingForMacrosMatchResult[k]).forEach(([k, val]) => {
        const isSelectType = !SearchMacrosProps.includes(k);
        if (isSelectType && (!availableFilters[k] || !availableFilters[k].some(vi => String(vi).toLowerCase() === String(val).toLowerCase()))) {
          availableFilters[k] = [...availableFilters[k] || [], val].sort();
        }
        const afv = (availableFilters[k] || []).filter(vi => {
          if (isSelectType) {
            return String(vi).toLowerCase() === String(val).toLowerCase();
          }
          return String(val).split('|').every(text => String(vi).toLowerCase().indexOf(text.toLowerCase()) !== -1);
        });
        if (afv.length) {
          selectedFilters[k] = afv;
        }
      });
      const keys = Object.keys(availableFilters);
      keys.forEach(k => {
        if (availableFilters[k].length === 1) {
          delete availableFilters[k];
        }
      });
      newSortingFields = [
        'woMemfisId', 'createdAt', 'updatedAt',
        ...Object.keys(availableFilters).filter(k => availableFilters[k].length > 1).sort(sortLabel)
      ];
    }
    setAvailableFitlers(availableFilters);
    setSelectedFilters(selectedFilters);
    setSortingFields(newSortingFields);
  }, [workOrderList, macrosMatchResult]);

  const handleSelectWo = (wo) => {
    onSelectWo(wo);
  };

  const handleViewWo = (wo) => {
    setSelectedWorkOrder(wo);
  };

  const handleCloseDetailsView = () => {
    setSelectedWorkOrder(null);
  };

  const handleSearch = () => {
    setAvailableFitlers({});
    setSelectedFilters({});
    onSearchWorkOrderList(showNameOrWoMemfis, showCode, episodeNumber);
  };

  const handleOnChangeShowNameOrWoMemfis = (e) => {
    setShowNameOrWoMemfis(e.target.value);
  };

  const handleOnChangeShowCode = (e) => {
    setShowCode(e.target.value);
  };

  const handleOnChangeEpisodeNumber = (e) => {
    setEpisodeNumber(e.target.value);
  };

  const handleSort = (s) => {
    const newSorting = {...sorting, ...s};
    setSorting(newSorting);
  };

  const renderSortingControls = () => {
    const showSortControls = workOrderList && workOrderList.length > 0;
    if (!showSortControls) {
      return null;
    }
    return (
      <ButtonGroup title={`Sort by ${prepareFieldLabel(sorting.field)}`}>
        <Button onClick={() => handleSort({direction: (sorting.direction === 'asc' ? 'desc' : 'asc')})}>
          {sorting.direction === 'asc'
            ? <i className="fa-solid fa-arrow-up-short-wide"></i>
            : <i className="fa-solid fa-arrow-down-wide-short"></i>
          }
        </Button>
        <DropdownButton id="bg-nested-dropdown" title="">
          {sortingFields.map(field => {
            const fieldLabel = prepareFieldLabel(field);
            return (
              <MenuItem
                key={field}
                title={`Sort by ${fieldLabel}`}
                onClick={() => handleSort({field})}
                active={field === sorting.field}
              >
                {fieldLabel}
              </MenuItem>
            )
          })}
        </DropdownButton>
      </ButtonGroup>
    );
  }

  const renderFilterBtn = () => {
    const showFilterBtn = Object.keys(availableFilters).length > 0;
    const inFilterMode = Object.keys(selectedFilters).length;
    if (showFilterBtn) {
      return (
        <ActionButton
          className='wo-filter-btn'
          onClick={() => setShowFilters(true)}
          title={inFilterMode ? 'Change filters' : 'Set filters'}
          primary={inFilterMode ? true : false}
        >
          Filter
        </ActionButton>
      );
    }
    return null;
  }

  const renderSearchControls = () => {
    return (
      <div>
        <FormGroup controlId="showNameOrWoMemfis">
          <ControlLabel>Show Name or WO MEMFIS</ControlLabel>
          <FormControl type="text" value={showNameOrWoMemfis} onChange={handleOnChangeShowNameOrWoMemfis} />
        </FormGroup>
        <FormGroup controlId="showCode">
          <ControlLabel>Show Code</ControlLabel>
          <FormControl type="text" value={showCode} onChange={handleOnChangeShowCode} />
        </FormGroup>
        <FormGroup controlId="episodeNumber">
          <ControlLabel>Episode Number</ControlLabel>
          <FormControl type="text" value={episodeNumber} onChange={handleOnChangeEpisodeNumber} />
        </FormGroup>
        <Button onClick={handleSearch}>Search</Button>
        {renderFilterBtn()}
        {renderSortingControls()}
      </div>
    );
  };

  const renderFilters = () => {
    if (!showFilters) {
      return null;
    }

    return (
      <div>
        <div>Filters</div>
        {Object.keys(availableFilters).map(k => {
          const filterValue = selectedFilters[k] || [];
          const selectableOptions = availableFilters[k].map(v => ({value: v, label: String(v)}));
          return (
            <div>
              <span>{prepareFieldLabel(k)}</span>
              <Select
                multi={true}
                clearable={true}
                value={filterValue}
                options={selectableOptions}
                onChange={v => {
                  const newSelFilters = {...selectedFilters};
                  newSelFilters[k] = v;
                  setSelectedFilters(newSelFilters);
                }}
              />
            </div>
          )
        })}
        <div>
          <ActionButton onClick={() => setShowFilters(false)}>Apply</ActionButton>
          <ActionButton onClick={() => {
            setShowFilters(false);
            setSelectedFilters({});
          }}>Clear</ActionButton>
        </div>
      </div>
    );
  }

  let filteredWOs = !workOrderList ? [] : filterArray(workOrderList, selectedFilters, macrosProperties);
  filteredWOs = sortArray(filteredWOs, sorting);

  return (
    <SelectContainerDiv>
      {workOrderSearchMode && <Header>
        <Title>Select Work Order</Title>
        <ActionButton onClick={onClose}>Back to Tags</ActionButton>
        </Header>
      }
      {workOrderSearchMode && !showFilters && renderSearchControls()}
      {workOrderSearchMode && renderFilters()}
      {isLoading && <LoadingMessage>Loading work orders...</LoadingMessage>}
      {!isLoading && !showFilters && workOrderSearchMode && workOrderList.length > 0 && (
        <Table>
          <TableHeader>
            <tr>
              <TableHeaderCell>WO Memfis</TableHeaderCell>
              <TableHeaderCell>Title</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {filteredWOs.map(wo => (
              <TableRow key={wo.woId} style={{
                backgroundColor: wo.qcStatusBgColor || '#ffffff',
                wordBreak: 'break-all'
              }}>
                <TableCell className="wo-memfis">{wo.woMemfisId}</TableCell>
                <TableCell className="title">{wo.title}</TableCell>
                <TableCell className="actions">
                  <ActionButton primary onClick={() => handleSelectWo(wo)}>
                    Link
                  </ActionButton>
                  <ActionButton onClick={() => handleViewWo(wo)}>View</ActionButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {!isLoading && workOrderSearchMode && workOrderList.length === 0 && (
        <EmptyMessage>No work orders found.</EmptyMessage>
      )}

      {!workOrderSearchMode && (
        <WorkOrderDetailsView
          workOrderData={selectedWorkOrder}
          onClose={handleCloseDetailsView}
        />
      )}
    </SelectContainerDiv>
  );
};

WorkOrderSearch.propTypes = {
  workOrderList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    woMemfisId: PropTypes.string,
    title: PropTypes.string,
  })),
  onSelectWo: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func,
  macrosMatchResult: PropTypes.object,
  macrosProperties: PropTypes.object,
  currentEmailData: PropTypes.object,
};

WorkOrderSearch.defaultProps = {
  workOrderList: [],
  isLoading: false,
};

export default WorkOrderSearch;